import { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Search, Calendar, Settings, ArrowRightFromLine, ArrowRightToLine } from 'lucide-react';
import { format, isToday, isTomorrow, parseISO } from 'date-fns';
import mixpanel from 'mixpanel-browser';
import { purpleColors, violetColors, slateColors, fontFamily } from '../ContentPages/AppLandingPage/styles';

// Import overlay components
import LocationPicker from './LocationPicker';
import DateTimeCard from './DateTimeCard';
import OptionsCard from './OptionsCard';
import PageHeader from '../PageHeader';
import SwitchButton from './SwitchButton';

// Container for the entire component
const Container = styled.div<{ screen: 'home' | 'journeys' }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.5rem;
  align-self: stretch;
  padding: 1rem;
  background: white;
  border-bottom: ${({ screen }) =>
    screen === 'journeys' ? `2px solid ${slateColors[200]}` : 'none'};
  
  @media (min-width: 768px) {
    padding: 1.5rem 2rem;
    gap: 0.75rem;
  }
`;

// Container for the controls - no card styling
const Controls = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.75rem;
  width: 100%;
  
  @media (min-width: 1024px) {
    flex-direction: row;
    align-items: center;
    gap: 1rem;
    flex-wrap: wrap;
  }
`;

// Mobile: Stacked layout, Desktop: Horizontal row
const LocationsGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  width: 100%;
  position: relative;
  
  @media (min-width: 640px) {
    flex-direction: row;
    align-items: center;
    gap: 0.75rem;
  }
  
  @media (min-width: 1024px) {
    flex: 1;
    min-width: 0;
    position: static;
  }
`;

// Compact location button
const LocationButton = styled.button<{ $type?: 'from' | 'to' }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  padding-right: 3.5rem; // Make room for switch button on narrow screens
  background: ${slateColors[50]};
  border: 2px solid ${slateColors[200]};
  border-radius: 0.75rem;
  cursor: pointer;
  transition: all 0.2s;
  text-align: left;
  flex: 1;
  min-width: 0;
  box-sizing: border-box;
  position: relative;
  
  &:hover {
    border-color: ${purpleColors[300]};
    background: ${slateColors[100]};
  }
  
  &:focus {
    outline: none;
    border-color: ${purpleColors[500]};
    box-shadow: 0 0 0 3px ${purpleColors[100]};
  }
  
  @media (min-width: 768px) {
    padding: 0.875rem 1.25rem;
    padding-right: 1.25rem; // Reset padding on wide screens
  }
  
  svg {
    width: 1.125rem;
    height: 1.125rem;
    color: ${purpleColors[600]};
    flex-shrink: 0;
  }
`;

const LocationLabel = styled.span<{ $type?: 'from' | 'to' }>`
  font-family: ${fontFamily};
  font-size: 0.9375rem;
  font-weight: 500;
  color: ${slateColors[500]};
  flex-shrink: 0;
  
  @media (min-width: 768px) {
    font-size: 1rem;
  }
`;

const LocationName = styled.span`
  font-family: ${fontFamily};
  font-size: 0.9375rem;
  font-weight: 500;
  color: ${slateColors[900]};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  min-width: 0;
  margin-left: -0.25rem; // Reduce gap between label and station name
  
  @media (min-width: 768px) {
    font-size: 1rem;
  }
`;

// Switch button wrapper - positions button to overlap fields on narrow screens
const SwitchButtonWrapper = styled.div`
  position: absolute;
  top: 50%;
  right: 0.75rem;
  transform: translateY(-50%);
  z-index: 10;
  
  @media (min-width: 640px) {
    position: static;
    transform: none;
    flex-shrink: 0;
  }
`;

// Controls group for date/time, options, and search button
const ControlsGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  width: 100%;
  
  @media (min-width: 640px) {
    flex-wrap: nowrap;
  }
  
  @media (min-width: 1024px) {
    flex: 0 0 auto;
    width: auto;
  }
`;

const DateTimeButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: ${slateColors[50]};
  border: 2px solid ${slateColors[200]};
  border-radius: 0.75rem;
  cursor: pointer;
  transition: all 0.2s;
  text-align: left;
  flex: 1;
  min-width: 0;
  box-sizing: border-box;
  
  &:hover {
    border-color: ${purpleColors[300]};
    background: ${slateColors[100]};
  }
  
  &:focus {
    outline: none;
    border-color: ${purpleColors[500]};
    box-shadow: 0 0 0 3px ${purpleColors[100]};
  }
  
  @media (min-width: 768px) {
    padding: 0.875rem 1.25rem;
    flex: 0 1 auto;
    min-width: 12rem;
  }
  
  svg {
    width: 1.125rem;
    height: 1.125rem;
    color: ${purpleColors[600]};
    flex-shrink: 0;
  }
`;

const DateTimeText = styled.span`
  font-family: ${fontFamily};
  font-size: 0.9375rem;
  font-weight: 500;
  color: ${slateColors[700]};
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  
  @media (min-width: 768px) {
    font-size: 1rem;
  }
`;

const OptionsButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem;
  background: ${slateColors[50]};
  border: 2px solid ${slateColors[200]};
  border-radius: 0.75rem;
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;
  width: 3rem;
  
  &:hover {
    border-color: ${purpleColors[300]};
    background: ${slateColors[100]};
  }
  
  &:focus {
    outline: none;
    border-color: ${purpleColors[500]};
    box-shadow: 0 0 0 3px ${purpleColors[100]};
  }
  
  @media (min-width: 768px) {
    padding: 0.875rem;
    width: 3.25rem;
  }
  
  svg {
    width: 1.125rem;
    height: 1.125rem;
    color: ${purpleColors[600]};
  }
`;

const SearchButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.875rem 1.5rem;
  background: linear-gradient(to right, ${purpleColors[600]}, ${violetColors[600]});
  color: white;
  border-radius: 0.75rem;
  font-family: ${fontFamily};
  font-weight: 600;
  font-size: 1rem;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
  width: 100%;
  flex-basis: 100%;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  
  &:hover {
    background: linear-gradient(to right, ${purpleColors[700]}, ${violetColors[600]});
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
    transform: translateY(-1px);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  svg {
    width: 1.125rem;
    height: 1.125rem;
  }
  
  @media (min-width: 640px) {
    flex-basis: auto;
    width: auto;
    flex: 0 0 auto;
  }
  
  @media (min-width: 1024px) {
    padding: 0.875rem 2rem;
    font-size: 1.0625rem;
  }
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 1rem;
  box-sizing: border-box;
`;

interface SearchControlsProps {
  setOrigin: (id: string) => void;
  setDestination: (id: string) => void;
  setDate: (date: string) => void;
  setOriginName: React.Dispatch<React.SetStateAction<string>>; 
  setDestinationName: React.Dispatch<React.SetStateAction<string>>; 
  setTripType: (type: 'departure' | 'arrival') => void;
  setOptions: (options: Record<string, string>) => void;
  fetchJourneys: (type: 'earlier' | 'later' | 'initial') => void;
  screen: 'home' | 'journeys';
  originId: string;
  destinationId: string;
  originName: string;
  destinationName: string;
  date: string;
  tripType: 'departure' | 'arrival';
  options: Record<string, string>;
}

const defaultOptions: Record<string, any> = {
  transfers: -1,
  transferTime: 0,
  accessibility: 'none',
  bike: false,
  longdistance: true,
  regional: true,
  suburban: true,
  local: true,
};

// Define the type for location details
interface Location {
  id: string;
  name: string;
}

const SearchControls = ({
  setOrigin, 
  setDestination, 
  setOriginName, 
  setDestinationName, 
  setDate, 
  setTripType, 
  setOptions, 
  fetchJourneys, 
  screen, 
  originId,
  originName,
  destinationId,
  destinationName,
  date,
  tripType,
  options
}: SearchControlsProps) => {
  
  const loadFromLocalStorage = useCallback(() => {
    const savedOrigin = localStorage.getItem('origin');
    const savedDestination = localStorage.getItem('destination');
    const savedOptions = localStorage.getItem('journeyOptions');

    if (savedOrigin && !originId) {
      const parsed = JSON.parse(savedOrigin);
      setOrigin(parsed.id);
      setOriginName(parsed.name);
    }

    if (savedDestination && !destinationId) {
      const parsed = JSON.parse(savedDestination);
      setDestination(parsed.id);
      setDestinationName(parsed.name);
    }

    if (savedOptions && Object.keys(options).length === 0) {
      setOptions(JSON.parse(savedOptions));
    } else {
      setOptions(defaultOptions);
    }
  }, [setOrigin, setOriginName, setDestination, setDestinationName, setOptions, originId, destinationId, options]);
  
  const [isLocationPickerOpen, setLocationPickerOpen] = useState(false);
  const [locationType, setLocationType] = useState<'from' | 'to'>('from');

  const [isDateTimeCardOpen, setDateTimeCardOpen] = useState(false);

  const [isOptionsCardOpen, setOptionsCardOpen] = useState(false);
  const [localOptions, setLocalOptions] = useState<Record<string, string>>(() => {
    const savedOptions = localStorage.getItem('journeyOptions');
    return savedOptions ? JSON.parse(savedOptions) : defaultOptions;
  });

  const [recentlyChosen, setRecentlyChosen] = useState<{ name: string; id: string }[]>(
    JSON.parse(localStorage.getItem('recentlyChosen') || '[]')
  );

  const [showControls, setShowControls] = useState(screen === 'home');

  useEffect(() => {
    if (!originId && !destinationId) {
      loadFromLocalStorage();
    }
  }, [originId, destinationId, loadFromLocalStorage]);

  useEffect(() => {
    if (Object.keys(options).length === 0) {
      loadFromLocalStorage();
    } else {
      // Sync localOptions with options prop (which may come from URL params)
      // This ensures OptionsCard displays the correct values from URL
      setLocalOptions(options);
    }
  }, [options, loadFromLocalStorage]);

  useEffect(() => {
    if (originId && originName) {
      localStorage.setItem('origin', JSON.stringify({ id: originId, name: originName }));
    }
    if (destinationId && destinationName) {
      localStorage.setItem('destination', JSON.stringify({ id: destinationId, name: destinationName }));
    }

    if (date) {
      localStorage.setItem('localDate', date);
    }
    if (tripType) {
      localStorage.setItem('localTripType', tripType);
    }
    
    if (Object.keys(options).length > 0) {
      localStorage.setItem('journeyOptions', JSON.stringify(options));
    }

    localStorage.setItem('recentlyChosen', JSON.stringify(recentlyChosen));
  }, [originId, originName, destinationId, destinationName, options, recentlyChosen, date, tripType]);

  const openOverlay = (type: 'from' | 'to') => {
    setLocationType(type);
    setLocationPickerOpen(true);
  };

  const closeOverlay = () => {
    setLocationPickerOpen(false);
    setOptionsCardOpen(false);
    setDateTimeCardOpen(false);
  };

  const handleLocationSelect = (location: Location) => {
    setRecentlyChosen((prev) => [location, ...prev].slice(0, 10));

    if (locationType === 'from') {
      setOrigin(location.id);
      setOriginName(location.name);
    } else {
      setDestination(location.id);
      setDestinationName(location.name);
    }

    closeOverlay();
  };

  const handleSwitchLocations = () => {
    setOrigin(destinationId);
    setDestination(originId);
    setOriginName(destinationName);
    setDestinationName(originName);
  };

  const handleDateTime = (newDateTime: string, newType: 'departure' | 'arrival') => {
    setDate(newDateTime);
    setTripType(newType);
    localStorage.setItem('localDate', newDateTime);
    localStorage.setItem('localTripType', newType);
    setDateTimeCardOpen(false);
  };

  const { t } = useTranslation();
  
  const getFormattedDateTime = () => {
    if (!date) {
      return t('common.departNow');
    }

    try {
      const parsedDate = parseISO(date);
      if (isNaN(parsedDate.getTime())) {
        return t('common.departNow');
      }

      const dateStr = isToday(parsedDate) 
        ? t('common.today')
        : isTomorrow(parsedDate) 
          ? t('common.tomorrow')
          : format(parsedDate, 'dd/MM');
      
      const timeStr = format(parsedDate, 'HH:mm');
      const atStr = t('common.at');
      return `${tripType === 'arrival' ? t('common.arrive') : t('common.depart')} ${dateStr} ${atStr} ${timeStr}`;
    } catch {
      return t('common.departNow');
    }
  };

  const handleOptions = (newOptions: Record<string, string>) => {
    setLocalOptions(newOptions);
    setOptions(newOptions);
    localStorage.setItem('journeyOptions', JSON.stringify(newOptions));
    setOptionsCardOpen(false);
  };

  const handleSearchClick = () => {
    mixpanel.track('Search Button Clicked', {
      originName,
      destinationName,
      date,
      tripType,
      options: localOptions,
    });

    fetchJourneys('initial');
  };

  const handleToggleClick = () => {
    setShowControls((prev) => !prev);
  };

  return (
    <Container screen={screen}>
      {screen === "journeys" && (
        <PageHeader
          breadcrumbs={[
            { label: t('common.home'), path: "/" },
            { label: t('pageHeader.journeys'), active: !showControls }
          ]}
          title={`${originName} ➜ ${destinationName}`}
          showTitleContainer={!showControls}
          subtitleWithChange={!showControls ? {
            text: getFormattedDateTime(),
            onChangeClick: handleToggleClick
          } : undefined}
        />
      )}
      {showControls && (
        <Controls>
          {/* Mobile: Stacked, Desktop: Horizontal row */}
          <LocationsGroup>
            <LocationButton onClick={() => openOverlay('from')} $type="from">
              <ArrowRightFromLine />
              <LocationLabel $type="from">{t('searchControls.from')}</LocationLabel>
              <LocationName>{originName || t('searchControls.selectStation')}</LocationName>
            </LocationButton>
            
            <SwitchButtonWrapper>
              <SwitchButton onClick={handleSwitchLocations} aria-label={t('searchControls.switchOriginDestination')} />
            </SwitchButtonWrapper>
            
            <LocationButton onClick={() => openOverlay('to')} $type="to">
              <ArrowRightToLine />
              <LocationLabel $type="to">{t('searchControls.to')}</LocationLabel>
              <LocationName>{destinationName || t('searchControls.selectStation')}</LocationName>
            </LocationButton>
          </LocationsGroup>
          
          <ControlsGroup>
            <DateTimeButton onClick={() => setDateTimeCardOpen(true)}>
              <Calendar />
              <DateTimeText>{getFormattedDateTime()}</DateTimeText>
            </DateTimeButton>
            <OptionsButton onClick={() => setOptionsCardOpen(true)}>
              <Settings />
            </OptionsButton>
            <SearchButton onClick={handleSearchClick}>
              <Search />
              {t('searchControls.findReliableTrains')}
            </SearchButton>
          </ControlsGroup>
          
          {(isLocationPickerOpen || isOptionsCardOpen || isDateTimeCardOpen) && (
            <Overlay>
              {isLocationPickerOpen && (
                <LocationPicker
                  onClose={closeOverlay}
                  onLocationSelect={handleLocationSelect}
                  recentlyChosen={recentlyChosen}
                />
              )}
              {isOptionsCardOpen && (
                <OptionsCard
                  onClose={closeOverlay}
                  onApply={handleOptions}
                  initialOptions={localOptions}
                />
              )}
              {isDateTimeCardOpen && (
                <DateTimeCard 
                  onClose={closeOverlay} 
                  onApply={handleDateTime} 
                  initialDate={date ? date.split('T')[0] : ''}
                  initialTime={date ? date.split('T')[1] : ''}
                  initialType={tripType}          
                />
              )}        
            </Overlay>
          )}
        </Controls>
      )}
    </Container>
  );
};

export default SearchControls;
